import React, { useRef, useCallback, useEffect, useMemo } from "react";
import { createButtonPressAnimation } from "../../../../utilities";
import { Sprite, Container, Text } from "@pixi/react";
import * as PIXI from "pixi.js";
import { TextStyle } from "pixi.js";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { SOUND_NAME } from "../../../../sounds";
import { ASSET_NAME } from "../../../../assets";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { FONT_CONFIG, TEXT_STYLE_CONFIG } from "../../../../textureConfigs";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";
import { setBet } from "../../../../State/actions";
import {
  selectBets,
  selectBalance,
  selectCriticalAnimationPlaying,
  selectIsSpinning,
  selectHoldAndWinActive,
  selectAutoPlayActive,
} from "../../../../State/selectors";

// Max bet button configuration - matches UI button dimensions
const MAX_BET_BUTTON_CONFIG = {
  BASE_WIDTH: 80,
  BASE_HEIGHT: 80,
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.97,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.75,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.3,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.1,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 1.0,
    },
  },
};

// Max bet button configuration for desktop - different dimensions
const MAX_BET_BUTTON_CONFIG_DESKTOP = {
  BASE_WIDTH: 93,
  BASE_HEIGHT: 64,
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 0.9,
    },
  },
};

const ButtonMaxBet = ({ toolbar }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { getResponsiveConfig, device, orientation, dimensions } =
    useDimensions();

  // Get bets and balance from Redux
  const bets = useSelector(selectBets);
  const balance = useSelector(selectBalance);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);
  const isSpinning = useSelector(selectIsSpinning);
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const autoPlayActive = useSelector(selectAutoPlayActive);

  // Add ref for the sprite to animate
  const buttonSpriteRef = useRef(null);

  // Create brightness filter for hover effect
  const brightnessFilter = useRef(new PIXI.filters.ColorMatrixFilter());
  brightnessFilter.current.brightness(2.0, false); // 30% brighter

  // Use ref for hover state to avoid unnecessary re-renders
  const isHoveredRef = useRef(false);

  // Add ref to track if button is currently processing a click
  const isProcessingClickRef = useRef(false);

  // Add ref to store timeout ID for proper cleanup
  const debounceTimeoutRef = useRef(null);

  // Calculate if button should be disabled - prevent during spins, critical animations, hold and win, or autoplay
  const isDisabled =
    criticalAnimationPlaying ||
    isSpinning ||
    holdAndWinActive ||
    autoPlayActive;

  const texture = PIXI.Assets.get(
    device === PixiSettings.DEVICE.DESKTOP
      ? ASSET_NAME.icon_popi_max_bet_landscape
      : ASSET_NAME.icon_popi_max_bet_portrait
  );

  // Get responsive configuration from context
  const buttonConfig =
    device === PixiSettings.DEVICE.DESKTOP
      ? getResponsiveConfig(MAX_BET_BUTTON_CONFIG_DESKTOP, true)
      : getResponsiveConfig(MAX_BET_BUTTON_CONFIG, true);

  // Get translation text - keep as single word, no breaks
  const translationText = t("max_btn").toUpperCase();
  const displayText = useMemo(() => {
    return translationText;
  }, [translationText]);

  // Calculate font size - different multipliers for desktop vs other devices
  // Use the smaller dimension to ensure text fits properly
  const idealFontSize = useMemo(() => {
    const buttonSize = Math.min(buttonConfig.width, buttonConfig.height);
    const multiplier = device === PixiSettings.DEVICE.DESKTOP ? 0.4 : 0.32; // Desktop: 0.4, Mobile/Tablet: 0.32
    return Math.round(buttonSize * multiplier);
  }, [buttonConfig.width, buttonConfig.height, device]);

  // Text style - white color, uppercase
  const textStyle = useMemo(() => {
    return new TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: idealFontSize,
      fontWeight: "500",
      fill: 0xffffff, // White color
      align: "center",
      ...TEXT_STYLE_CONFIG.OUTLINE,
    });
  }, [idealFontSize]);

  // Calculate responsive button position based on device and orientation
  // Uses toolbar.layout.maxBet.x from hook for X position
  const buttonPosition = (() => {
    // Get X position from toolbar layout if available, otherwise fallback to old logic
    const containerX = toolbar.layout?.maxBet?.x ?? (() => {
      if (device === PixiSettings.DEVICE.MOBILE) {
        if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
          return buttonConfig.width / 1.5;
        } else {
          return toolbar.width - buttonConfig.width * 0.775;
        }
      } else if (device === PixiSettings.DEVICE.TABLET) {
        if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
          return buttonConfig.width * 0.9;
        } else {
          return toolbar.width - buttonConfig.width * 0.875;
        }
      } else {
        return buttonConfig.width * 9.;
      }
    })();

    // Calculate Y position based on device and orientation
    let containerY;
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        containerY = -dimensions.height / 5.2 + buttonConfig.height / 2;
      } else {
        containerY = -dimensions.height / 2 - buttonConfig.height * 1.8;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        containerY = -dimensions.height / 5.2 + buttonConfig.height / 2;
      } else {
        // Landscape: center the button vertically in the toolbar
        containerY = toolbar.height / 2;
      }
    } else {
      containerY = toolbar.height / 2;
    }

    return {
      x: containerX,
      y: containerY,
    };
  })();

  const handleClick = () => {
    // Disable button during critical animations or spinning
    if (isDisabled) {
      return;
    }

    // Prevent rapid clicks if already processing
    if (isProcessingClickRef.current) {
      return;
    }

    // Clear any existing timeout before starting new one
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    isProcessingClickRef.current = true;

    soundManager.play(SOUND_NAME.button_click);

    // Find the highest bet that doesn't exceed balance
    const affordableBets = bets.filter((bet) => bet <= balance);

    if (affordableBets.length > 0) {
      const maxBet = Math.max(...affordableBets);
      dispatch(setBet(maxBet));
    }

    // Add press animation
    if (buttonSpriteRef.current) {
      createButtonPressAnimation(buttonSpriteRef.current);
    }

    // Reset processing flag after animation completes (500ms debounce)
    debounceTimeoutRef.current = setTimeout(() => {
      isProcessingClickRef.current = false;
    }, 500);
  };

  // Hover effect handlers - using refs for direct sprite manipulation
  const handlePointerOver = useCallback(() => {
    // Don't apply hover effect if button is disabled
    if (isDisabled) {
      return;
    }
    if (buttonSpriteRef.current) {
      isHoveredRef.current = true;
      // Directly apply brightness filter to sprite
      if (
        !buttonSpriteRef.current.filters ||
        buttonSpriteRef.current.filters.length === 0
      ) {
        buttonSpriteRef.current.filters = [brightnessFilter.current];
      }
    }
  }, [buttonSpriteRef, brightnessFilter, isDisabled]);

  const handlePointerOut = useCallback(() => {
    if (buttonSpriteRef.current) {
      isHoveredRef.current = false;
      // Directly remove brightness filter from sprite
      buttonSpriteRef.current.filters = [];
    }
  }, [buttonSpriteRef]);

  // Remove hover effects when button becomes disabled
  useEffect(() => {
    if (isDisabled && buttonSpriteRef.current) {
      buttonSpriteRef.current.filters = [];
      isHoveredRef.current = false;
    }
  }, [isDisabled]);

  // Clean up hover effects and debounce timeout on unmount
  useEffect(() => {
    const sprite = buttonSpriteRef.current;
    return () => {
      if (sprite) {
        sprite.filters = [];
      }
      // Clear any pending debounce timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Don't render MaxBet button on tablet landscape
  if (
    device === PixiSettings.DEVICE.TABLET &&
    orientation === PixiSettings.ORIENTATION.LANDSCAPE
  ) {
    return null;
  }

  return (
    <Container
      name="secondary_button_max_bet_container"
      x={buttonPosition.x}
      y={buttonPosition.y}
      eventMode={isDisabled ? "none" : "static"}
      cursor={isDisabled ? "default" : "pointer"}
      hitArea={
        new PIXI.Rectangle(
          -buttonConfig.width / 2,
          -buttonConfig.height / 2,
          buttonConfig.width,
          buttonConfig.height
        )
      }
      onpointertap={handleClick}
      onpointerover={handlePointerOver}
      onpointerout={handlePointerOut}
    >
      <Sprite
        ref={buttonSpriteRef}
        texture={texture}
        name="secondary_button_max_bet"
        anchor={0.5}
        width={buttonConfig.width}
        height={buttonConfig.height}
        x={0}
        y={0}
        eventMode="none"
        alpha={
          isDisabled
            ? 0.7
            : device === PixiSettings.DEVICE.MOBILE ||
              (device === PixiSettings.DEVICE.TABLET &&
                orientation === PixiSettings.ORIENTATION.PORTRAIT)
            ? 0.7
            : 1
        }
      />
      <Text
        text={displayText}
        style={textStyle}
        anchor={0.5}
        x={0}
        y={0}
        resolution={2}
        eventMode="none"
      />
    </Container>
  );
};

export default ButtonMaxBet;
